import BlogLayout, { P, H2b, H3b, UL, LI } from './BlogLayout';
import { posts } from './posts';

const meta = posts.find((p) => p.slug === 'how-to-get-custom-metal-parts-made-ontario');

export default function HowToGetCustomMetalParts() {
  return (
    <BlogLayout title={meta.title} description={meta.description}>
      <P>
        Getting a custom metal part made used to mean owning CAD software, knowing how to use it, or paying an engineer to draft one before a shop would even quote you.
        That is no longer true. Ontario's fabrication network regularly quotes and builds parts from nothing more than a hand sketch or a photo of a napkin drawing.
        Here is exactly how the process works, step by step.
      </P>

      <H2b>1. Know what you need before you start</H2b>
      <P>
        Before sketching anything, get clear on the basics. You do not need exact specifications, but you should be able to answer:
      </P>
      <UL>
        <LI>What is the part for, and where does it need to fit or attach?</LI>
        <LI>Roughly how big is it (length, width, height, or diameter)?</LI>
        <LI>What material makes sense — stainless steel, aluminum, mild steel?</LI>
        <LI>How many do you need — one prototype, or a production run?</LI>
      </UL>
      <P>
        Even rough answers here save time later. A fabricator can refine dimensions and material choice with you, but they need a starting point.
      </P>

      <H2b>2. Create a sketch, not a CAD file</H2b>
      <P>
        A hand sketch, a photo of a whiteboard drawing, or an annotated photo of an existing part is enough to begin. The goal is to communicate shape and intent,
        not to produce a manufacturing drawing. Include whatever you can of the following directly on the sketch:
      </P>
      <UL>
        <LI>Approximate dimensions, even if written as ranges</LI>
        <LI>Material preference, if you have one</LI>
        <LI>Quantity needed</LI>
        <LI>Intended use case, so the fabricator understands load, environment, or finish requirements</LI>
      </UL>
      <P>
        A structured brief — with concept renderings built from your sketch — gets you further, faster, than trying to learn CAD just to request a quote.
      </P>

      <H2b>3. Find the right fabricator type for your part</H2b>
      <P>
        Not every metal shop does every kind of work. Knowing which category your part falls into helps you avoid getting quotes from shops that are not a fit.
      </P>
      <H3b>CNC machining</H3b>
      <P>
        Best for parts that need tight tolerances, precise features like threaded holes or pockets, or are machined from solid stock. Common for mechanical components and parts with fine detail.
      </P>
      <H3b>Sheet metal fabrication</H3b>
      <P>
        Best for parts made from flat sheet stock that is cut, bent, and formed — brackets, enclosures, panels, and shelving. Generally faster and less expensive than machining for this kind of geometry.
      </P>
      <H3b>Welding and welded assemblies</H3b>
      <P>
        Best for parts built by joining multiple pieces — frames, structural supports, custom fixtures. Often combined with sheet metal or machined components in a single assembly.
      </P>

      <H2b>4. Get multiple quotes</H2b>
      <P>
        Pricing, lead time, and capability vary significantly between shops, even for the same part. Getting quotes from two or three fabricators — ideally in a
        comparable, structured format — is the only way to know whether a price is reasonable and whether a shop is actually set up to run your job well.
      </P>

      <H2b>5. Evaluate quotes beyond price</H2b>
      <P>The lowest quote is not automatically the right choice. Before deciding, weigh:</P>
      <UL>
        <LI>Turnaround time and current shop capacity</LI>
        <LI>Experience with your specific material and process</LI>
        <LI>Whether material certifications or finish specs are included</LI>
        <LI>Communication — how clearly they answer questions before you commit</LI>
        <LI>Track record on similar orders, not just price per part</LI>
      </UL>
      <P>
        A slightly higher quote from a shop that understands your part and communicates clearly is usually a better outcome than the cheapest bid from a shop that does not.
      </P>
    </BlogLayout>
  );
}
